import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, Lock, RotateCcw } from "lucide-react";

import { postRest } from "./config";

import {
  challengeLabels,
  challengeRecommendations,
  getObservations,
  getResult,
  getSnapshotItems,
  questions,
  snapshotLevels,
  CHALLENGE_Q,
  MILESTONE_Q,
  REVENUE_Q,
  getHeadline,
  type AssessmentTone,
} from "@/data/assessment";

/**
 * Ported from the Lovable build. Scoring, copy and layout are unchanged - the
 * only differences are the three things that cannot survive the platform move:
 *
 *  1. react-router <Link> becomes a plain anchor; URLs arrive as props so
 *     WordPress owns its own permalinks.
 *  2. The `retainers` array was imported from the Services page. Packages are
 *     a CPT now, so the three tiers arrive as a JSON prop. If they are missing
 *     the suggested-level block still renders the tier name - that is derived
 *     from the answers, not content - and simply omits the package copy rather
 *     than falling back to a hardcoded duplicate that could drift from the CMS.
 *  3. supabase.functions.invoke becomes a POST to /ozria/v1/assessment-lead.
 *
 * One deliberate departure: the question-to-question slide was framer-motion,
 * which costs ~110 kB gzipped - more than the rest of this bundle combined, for
 * a single 280ms transition. It is now the `.slide-in` keyframe in main.css.
 * The visible difference is that the outgoing question no longer animates out;
 * framer’s mode="wait" ran exit and enter in sequence, so the new behaviour is
 * also about half a second quicker between questions.
 */

interface TierRecord {
  id: string;
  name: string;
  tagline: string;
}

interface ReadinessAssessmentProps {
  /** JSON array of { id, name, tagline } for bronze / silver / gold. */
  tiers?: string;
  bookingUrl?: string;
  privacyUrl?: string;
  contactEmail?: string;
  /*
   * Proof the page was rendered by the site, minted server-side and posted
   * back with the answers. A script that found the endpoint and posts to it
   * directly cannot produce one, which is what almost all form spam is.
   */
  token?: string;
  /*
   * The Turnstile site key, when the challenge is switched on. Empty means it
   * is not, and the component renders no widget and sends no solve - which is
   * how the site behaves until somebody pastes a key into the settings.
   */
  turnstileKey?: string;
}

const parseTiers = (raw?: string): TierRecord[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is TierRecord =>
        !!t && typeof t === "object" && typeof (t as TierRecord).id === "string"
    );
  } catch {
    console.warn("[ozria] Could not parse assessment tiers");
    return [];
  }
};

const yearEnds = [
  { label: "31 March", month: 3, day: 31 },
  { label: "30 June", month: 6, day: 30 },
  { label: "30 September", month: 9, day: 30 },
  { label: "31 December", month: 12, day: 31 },
];

const labelClass = "font-eyebrow text-[hsl(var(--on-ink)/0.5)] block mb-3";

const toneColor = (tone: AssessmentTone) => {
  switch (tone) {
    case "optimised":
    case "strong":
      return "hsl(var(--green-bright))";
    case "developing":
      return "hsl(var(--on-ink))";
    default:
      return "hsl(var(--amber-bright))";
  }
};

const weeksUntil = (month: number, day: number) => {
  const now = new Date();
  let target = new Date(now.getFullYear(), month - 1, day);
  if (target.getTime() <= now.getTime()) {
    target = new Date(now.getFullYear() + 1, month - 1, day);
  }
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 7)));
};

const TOTAL_STEPS = questions.length + 1;

const ReadinessAssessment = ({
  tiers,
  bookingUrl = "/booking",
  privacyUrl = "/privacy-policy",
  contactEmail = "sales@ozriaconsulting.com",
  token = "",
  turnstileKey = "",
}: ReadinessAssessmentProps) => {
  const tierRecords = useMemo(() => parseTiers(tiers), [tiers]);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [yearEnd, setYearEnd] = useState("31 December");
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [turnstile, setTurnstile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const reportRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLHeadingElement>(null);
  const blurredRef = useRef<HTMLDivElement>(null);

  // Keep the blurred preview out of the tab order and the accessibility tree.
  useEffect(() => {
    const node = blurredRef.current;
    if (!node) return;
    node.setAttribute("inert", "");
    node
      .querySelectorAll<HTMLElement>("a, button, input, select, textarea, [tabindex]")
      .forEach((el) => el.setAttribute("tabindex", "-1"));
  }, [revealed, done]);

  useEffect(() => {
    if (revealed) reportRef.current?.focus();
  }, [revealed]);

  /*
   * key={step} remounts the question, which threw focus back to the top of the
   * document on every answer - seven times, for anybody using a keyboard. Not
   * on the first render, or arriving at the section would yank the page.
   */
  const started = useRef(false);
  useEffect(() => {
    if (done) return;
    if (!started.current) {
      started.current = true;
      return;
    }
    questionRef.current?.focus();
  }, [step, done]);


  const handleAnswer = (optionIndex: number) => {
    const nextAnswers = [...answers];
    const nextSelected = [...selectedOptions];
    nextAnswers[step] = questions[step].scores[optionIndex];
    nextSelected[step] = optionIndex;
    setAnswers(nextAnswers);
    setSelectedOptions(nextSelected);
    setStep(step + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setSelectedOptions([]);
    setYearEnd("31 December");
    setDone(false);
    setEmail("");
    setHoneypot("");
    setSaveFailed(false);
    setSentTo("");
    setSubmitting(false);

    setEmailError("");
    setRevealed(false);
  };

  const totalScore = answers.reduce((a, b) => a + b, 0);
  const maxScore = questions.length * 4;
  const result = getResult(totalScore);
  const readiness = Math.round((totalScore / maxScore) * 100);
  const challengeIndex = selectedOptions[CHALLENGE_Q] ?? 4;
  const observations = useMemo(
    () => getObservations(answers, selectedOptions),
    [answers, selectedOptions]
  );
  const snapshot = useMemo(() => getSnapshotItems(selectedOptions), [selectedOptions]);

  const weeks = useMemo(() => {
    const option = yearEnds.find((y) => y.label === yearEnd) ?? yearEnds[3];
    return weeksUntil(option.month, option.day);
  }, [yearEnd]);

  const chip = useMemo(() => {
    if (weeks >= 26) return { text: "Time to get ahead", color: "hsl(var(--green-bright))" };
    if (weeks >= 12) return { text: "Plan the close now", color: "hsl(var(--amber-bright))" };
    return { text: "Tight - act quickly", color: "#F2846B" };
  }, [weeks]);

  const tier = useMemo(() => {
    const revenue = selectedOptions[REVENUE_Q];
    const milestone = selectedOptions[MILESTONE_Q];
    if (revenue === undefined) return null;
    /*
     * Matched against the ids in the tiers prop, which the front page now
     * builds from ozria_tier_anchor() rather than from the stored tier id.
     * The stored ids are still bronze/silver/gold; nothing that reaches the
     * browser is, including this attribute - it was the last place the old
     * naming appeared in the page source.
     */
    // The revenue answers are the levels' own bands: under £1m, £1m–£5m,
    // then £5m and over.
    let id = "growth";
    if (revenue === 0) id = "foundations";
    else if (revenue === 1) id = milestone !== undefined && milestone >= 2 ? "partner" : "growth";
    else id = "partner";
    const record = tierRecords.find((r) => r.id === id);
    /*
     * The level’s real name, not the metal.
     *
     * This used to title-case the id, so the headline result of the assessment
     * read "Gold" - the naming the packages were moved off weeks ago - with
     * "Partner" in smaller type beneath it. The stored ids stay bronze/silver/
     * gold because the scoring above matches on them and renaming them is a
     * data migration for no visible gain; nothing a visitor reads carries them.
     *
     * The fallback matters: if the tiers prop is missing the block still has to
     * say something, and it can no longer invent a name from the id.
     */
    return {
      name: record?.name ?? "",
      tagline: record?.tagline ?? "",
    };
  }, [selectedOptions, tierRecords]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
    if (!valid) {
      setEmailError("Please enter a valid email address, like you@company.com.");
      return;
    }
    setEmailError("");

    // Bot filled the hidden field - reveal the report, store nothing.
    if (honeypot.trim() !== "") {
      setRevealed(true);
      return;
    }

    setSubmitting(true);
    try {
      await postRest("assessment-lead", {
        email: email.trim(),
        honeypot,
        token,
        turnstile,
        answers: questions.map((q, i) => ({
          question: q.question,
          answer: q.options[selectedOptions[i]] ?? "",
          option_index: selectedOptions[i] ?? 0,
        })),
        score: totalScore,
        max_score: maxScore,
        maturity_level: result.level,
        readiness_pct: readiness,
        year_end: yearEnd,
        weeks_to_year_end: weeks,
        suggested_tier: tier?.name ?? "",
        // The report itself, so the emails can carry what the visitor is
        // looking at rather than a score and a guess. Derived values only -
        // nothing here is scored or worded differently from the screen.
        summary: result.summary,
        recommendation: result.recommendation,
        challenge: challengeLabels[challengeIndex],
        challenge_recommendation: challengeRecommendations[challengeIndex],
        observations,
        snapshot: snapshot.map((s) => ({ label: s.label, level: s.level })),
        // Which page it was taken on. The assessment is embedded in more than
        // one place, and "where were they when they did this" is the first
        // thing anyone asks about a lead.
        page: location.pathname + location.search,
      });
      setSaveFailed(false);
      setSentTo(email.trim());
    } catch (err) {
      console.error("Could not save the assessment lead:", err);
      setSaveFailed(true);
    } finally {
      setSubmitting(false);
      setRevealed(true);
    }
  };


  /*
   * Progress into the step, not up to it. At step 0 this was 0% - a 2px track
   * with nothing in it, which reads as a divider - so the first screen, the one
   * that has to persuade somebody to start, showed no sense of length at all.
   */
  const progress = done ? 100 : ((step + 1) / TOTAL_STEPS) * 100;

  /*
   * The verdict, then what to do, then the working.
   *
   * This ran in the order the report is generated - summary, observations,
   * opportunity, snapshot, and the recommendation last, nineteen hundred pixels
   * down on a laptop and nearly seven thousand on a phone. The one thing
   * somebody could act on was the furthest from the top, and the only button in
   * the whole report was "Start again".
   */
  const fullReport = (
    <>
      <p className="max-w-[46rem] leading-relaxed text-[hsl(var(--on-ink)/0.78)]">
        {result.summary}
      </p>

      {/* Next step, and the thing to do about it */}
      <div className="mt-10">
        <div className="flex items-center gap-4 mb-4">
          <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
            Recommended Next Step
          </span>
          <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
        </div>
        <p className="max-w-[46rem] leading-relaxed text-[hsl(var(--on-ink)/0.78)]">
          {result.recommendation}
        </p>
        <a
          href={bookingUrl}
          className="inline-flex items-center gap-2 mt-6 bg-[hsl(var(--green-bright))] text-[hsl(var(--ink))] px-8 py-4 text-[11px] font-semibold tracking-widest uppercase rounded-lg hover:bg-[hsl(var(--on-ink))] transition-colors"
        >
          Book a discovery call <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>

      <div className="mt-12 h-[1px] w-full bg-[rgba(255,255,255,0.14)]" />

      {/* Observations */}
      <div className="mt-10">
        <div className="flex items-center gap-4 mb-2">
          <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
            Key Observations
          </span>
          <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
        </div>
        {/* Held to a measure. These ran the full width of the section, which on
            a laptop is about a hundred and fifty characters a line. */}
        <ul className="max-w-[46rem]">
          {observations.map((o) => (
            <li
              key={o}
              className="flex items-start gap-4 py-5 border-b"
              style={{ borderColor: "rgba(255,255,255,0.1)" }}
            >
              <Check
                size={16}
                className="mt-1 shrink-0 text-[hsl(var(--green-bright))]"
                aria-hidden="true"
              />
              <span className="text-sm leading-relaxed text-[hsl(var(--on-ink)/0.78)]">{o}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Primary opportunity */}
      <div className="mt-10">
        <div className="flex items-center gap-4 mb-4">
          <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
            Primary Opportunity Area
          </span>
          <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
        </div>
        <h3
          className="font-heading font-semibold text-[hsl(var(--on-ink))]"
          style={{ fontSize: "clamp(1.35rem, 2.6vw, 1.6rem)", lineHeight: 1.2 }}
        >
          {challengeLabels[challengeIndex]}
        </h3>
        <p className="mt-4 max-w-[46rem] leading-relaxed text-[hsl(var(--on-ink)/0.78)]">
          {challengeRecommendations[challengeIndex]}
        </p>
      </div>

      {/* Snapshot */}
      <div className="mt-10">
        <div className="flex items-center gap-4 mb-3">
          <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
            Financial Infrastructure Snapshot
          </span>
          <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
        </div>
        {/* The scale, once. It was printed under each of the four, so the same
            eleven words appeared four times in 11px mono. */}
        <p className="mb-6 font-mono text-[11px] text-[hsl(var(--on-ink)/0.55)]">
          {snapshotLevels.join(" · ")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {snapshot.map((item) => (
            <div key={item.label}>
              <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">{item.label}</p>
              <p className="font-heading font-semibold text-lg text-[hsl(var(--on-ink))] mt-3">
                {item.level}
              </p>
              <div
                className="mt-4 flex gap-1.5"
                role="img"
                aria-label={`${item.level}, ${item.score} of ${snapshotLevels.length}`}
              >
                {[0, 1, 2, 3].map((sc) => (
                  <span
                    key={sc}
                    className="h-[3px] flex-1"
                    style={{
                      backgroundColor:
                        sc < item.score ? "hsl(var(--green-bright))" : "rgba(255,255,255,0.12)",
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const headerBlock = (
    <div>
      <div className="flex items-center gap-4 mb-5">
        <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
          Readiness assessment
        </span>
        <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
      </div>
      <h2
        className="font-heading font-semibold text-[hsl(var(--on-ink))]"
        style={{ fontSize: "clamp(1.9rem, 3.1vw, 2.5rem)", lineHeight: 1.08 }}
      >
        How ready are you for{" "}
        <span className="italic font-light text-[hsl(var(--on-ink)/0.6)]">year end</span>?
      </h2>
      {/*
        * The pitch, hidden on a phone once somebody has started.
        *
        * The eyebrow, a two-line headline, four lines of intro and a rule come
        * to about 640px, which on a 390px screen put question one below the
        * fold on arrival and kept it there for all eight steps. It earns that
        * room once. After the first answer the question gets the screen.
        */}
      <p className={`mt-4 max-w-[26rem] text-[15px] leading-relaxed text-[hsl(var(--on-ink)/0.68)] ${step > 0 ? "hidden lg:block" : ""}`}>
        Most audit pain is created months before the auditors arrive. Tell us where you stand and we’ll show you what’s left to do - and which level of support fits.
      </p>
      {/* The first thing anybody wants to know about a multi-step form. */}
      <p className={`mt-3 font-eyebrow text-[hsl(var(--on-ink)/0.5)] ${step > 0 ? "hidden lg:block" : ""}`}>
        Seven questions &middot; About two minutes
      </p>

      {/* Progress meter */}
      <div className="mt-8 h-[2px] w-full bg-[rgba(255,255,255,0.12)]">
        <div
          className="h-full bg-[hsl(var(--green-bright))] motion-safe:transition-[width] motion-safe:duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );

  /*
     * No id here. The PHP wrapper carries it, and it has to: this section does
   * not exist until the island mounts, which happens on scroll - so a visitor
   * clicking "Assessment" in the nav on a fresh page would have jumped at
   * nothing. Having both was two elements with one id, which is invalid, and
   * the browser resolved the anchor to the wrapper anyway - so the scroll offset
   * tuned on this one never applied to the jump that uses it.
   */
  return (
    <section className="relative bg-ink overflow-hidden">

      <div className="relative container mx-auto px-6 py-24">
        {/*
          * items-start, not items-center. Centred, the two columns floated
          * against a lot of empty ink on a laptop.
          */}
        {!done ? (
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start">
            {headerBlock}

            <div>
              {/* key={step} remounts the subtree, so the keyframe replays. */}
              <div key={step} className="slide-in">
                {/*
                  * "of 7", because there are seven questions - the eighth step
                  * is the year end, which is not one. And a live region, so a
                  * screen reader is told the step changed rather than being
                  * left on a page that silently replaced itself.
                  */}
                <p className="font-eyebrow font-mono text-[hsl(var(--on-ink)/0.5)]" role="status">
                  {step < questions.length
                    ? `Question ${step + 1} of ${questions.length}`
                    : "Last step"}
                </p>

                {step < questions.length ? (
                  <>
                    <h3
                      id="assessment-question"
                      ref={questionRef}
                      tabIndex={-1}
                      className="font-heading font-semibold text-[hsl(var(--on-ink))] mt-4 focus:outline-none"
                      style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.5rem)", lineHeight: 1.2 }}
                    >
                      {questions[step].question}
                    </h3>
                    {/*
                      * A radio group, not a row of toggle buttons. aria-pressed
                      * describes something you switch on and off; this is one
                      * choice out of four, and that is what a screen reader
                      * should hear. Arrow keys move between them, which is what
                      * radios promise.
                      */}
                    {/* Capped: at 1440 the answers stretched to eleven hundred pixels -
                      * a 50px-tall button a metre wide reads as a divider, not a choice. */}
                    <div className="mt-6 space-y-2 max-w-[40rem]" role="radiogroup" aria-labelledby="assessment-question">
                      {questions[step].options.map((opt, i) => {
                        const selected = selectedOptions[step] === i;
                        return (
                          <button
                            key={opt}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onKeyDown={(e) => {
                              if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
                              e.preventDefault();
                              const all = e.currentTarget.parentElement?.querySelectorAll("button");
                              if (!all) return;
                              const next = (i + (e.key === "ArrowDown" ? 1 : all.length - 1)) % all.length;
                              (all[next] as HTMLElement).focus();
                            }}
                            onClick={() => handleAnswer(i)}
                            className={`group w-full flex items-center gap-4 text-left px-4 py-3 border rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--green-bright))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--ink))] ${
                              selected
                                ? "bg-[hsl(var(--green-bright))] text-[hsl(var(--ink))] border-[hsl(var(--green-bright))]"
                                : "bg-transparent text-[hsl(var(--on-ink)/0.82)] border-[rgba(255,255,255,0.2)] hover:bg-[hsl(var(--green-bright))] hover:text-[hsl(var(--ink))] hover:border-[hsl(var(--green-bright))]"
                            }`}
                          >
                            <span
                              className={`shrink-0 w-6 h-6 flex items-center justify-center font-mono text-xs border ${
                                selected
                                  ? "border-[hsl(var(--ink)/0.35)]"
                                  : "border-[rgba(255,255,255,0.25)] group-hover:border-[hsl(var(--ink)/0.35)]"
                              }`}
                            >
                              {String.fromCharCode(65 + i)}
                            </span>
                            <span className="text-[15px] leading-snug">{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h3
                      className="font-heading font-semibold text-[hsl(var(--on-ink))] mt-4"
                      style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.5rem)", lineHeight: 1.2 }}
                    >
                      Your financial year end
                    </h3>
                    <div className="mt-6 max-w-md">
                      {/* The h3 above already says these words; twice in a row
                        * reads as a stutter. The label stays for the screen
                        * reader, invisibly. */}
                      <label className="sr-only" htmlFor="assessment-year-end">
                        Your financial year end
                      </label>
                      <select
                        id="assessment-year-end"
                        value={yearEnd}
                        onChange={(e) => setYearEnd(e.target.value)}
                        className="w-full rounded-lg px-4 py-3 text-[hsl(var(--on-ink))] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--green-bright))]"
                      >
                        {yearEnds.map((y) => (
                          <option key={y.label} value={y.label} className="text-foreground">
                            {y.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setDone(true)}
                        className="mt-6 inline-flex items-center gap-2 bg-[hsl(var(--green-bright))] text-[hsl(var(--ink))] px-8 py-3 text-[11px] font-semibold tracking-widest uppercase rounded-lg hover:bg-[hsl(var(--on-ink))] transition-colors"
                      >
                        See my results <ArrowRight size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>

            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="mt-6 inline-flex items-center gap-2 min-h-[44px] px-1 text-sm text-[hsl(var(--on-ink)/0.65)] underline underline-offset-4 hover:text-[hsl(var(--on-ink))] transition-colors"
              >
                <RotateCcw size={14} aria-hidden="true" className="rotate-90" />
                Back a question
              </button>
            )}
            </div>
          </div>
        ) : (
          <div aria-live="polite">
            <div className="flex items-center gap-4 mb-5">
              <span className="font-eyebrow text-[hsl(var(--on-ink)/0.5)] whitespace-nowrap">
                Readiness assessment
              </span>
              <span className="h-[1px] flex-1 bg-[rgba(255,255,255,0.14)]" />
            </div>
            {/* The question has just been answered; the heading should not keep asking it. */}
            <h2
              className="font-heading font-semibold text-[hsl(var(--on-ink))]"
              style={{ fontSize: "clamp(1.7rem, 2.6vw, 2.1rem)", lineHeight: 1.1 }}
            >
              Your <span className="italic font-light text-[hsl(var(--on-ink)/0.6)]">results</span>
            </h2>

            {/* Maturity band */}
            <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">Financial maturity</p>
                <p
                  className="font-heading font-semibold mt-2"
                  style={{
                    color: toneColor(result.tone),
                    fontSize: "clamp(2rem, 3.6vw, 2.7rem)",
                    lineHeight: 1.03,
                  }}
                >
                  {result.level}
                </p>
              </div>
              {/*
                * The score, with the unit on it. It sat here as a bare "13 of
                * 28" while the same number appeared as 46% two inches below -
                * one figure said twice, neither explaining the other. The
                * percentage is the headline; this is what it is a percentage of.
                */}
              <p className="font-mono text-sm tabular text-[hsl(var(--on-ink)/0.7)]">
                {totalScore} of {maxScore} points
              </p>
            </div>
            <div className="mt-4 h-[2px] w-full bg-[rgba(255,255,255,0.12)]">
              <div
                className="h-full motion-safe:transition-[width] motion-safe:duration-500"
                style={{ width: `${readiness}%`, backgroundColor: toneColor(result.tone) }}
              />
            </div>


            {/* Three-column band */}
            <div
              className="mt-8 grid grid-cols-1 md:grid-cols-3 border-t"
              style={{ borderColor: "rgba(255,255,255,0.12)" }}
            >
              <div className="py-6 md:pr-8">
                <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">Audit readiness</p>
                <p
                  className="font-heading font-semibold tabular text-[hsl(var(--green-bright))] mt-3"
                  style={{ fontSize: "clamp(1.9rem, 3.2vw, 2.5rem)", lineHeight: 1.05 }}
                >
                  {readiness}%
                </p>
                {/*
                  * No second bar. The one under the maturity band is already
                  * this number drawn on a scale; two tracks showing one value
                  * is a reader wondering what the difference is.
                  */}
              </div>

              <div
                className="py-6 md:px-8 border-t md:border-t-0 md:border-l md:border-r"
                style={{ borderColor: "rgba(255,255,255,0.12)" }}
              >
                <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">Until year end</p>
                <p
                  className="font-heading font-semibold text-[hsl(var(--green-bright))] mt-3"
                  style={{ fontSize: "clamp(1.9rem, 3.2vw, 2.5rem)", lineHeight: 1.05 }}
                >
                  <span className="tabular">{weeks}</span> weeks
                </p>
                {/*
                  * A dot and a word, where this was a bordered box that looked
                  * like a button and did nothing when pressed.
                  */}
                <span className="inline-flex items-center gap-2 mt-4 font-eyebrow" style={{ color: chip.color }}>
                  <span
                    aria-hidden="true"
                    className="inline-block h-[7px] w-[7px] rounded-full"
                    style={{ backgroundColor: chip.color }}
                  />
                  {chip.text}
                </span>
              </div>

              <div
                className="py-6 md:pl-8 border-t md:border-t-0"
                style={{ borderColor: "rgba(255,255,255,0.12)" }}
              >
                <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">Suggested level</p>
                {revealed ? (
                  <>
                    <p
                      className="font-heading font-semibold text-[hsl(var(--green-bright))] mt-3"
                      style={{ fontSize: "clamp(1.9rem, 3.2vw, 2.5rem)", lineHeight: 1.05 }}
                    >
                      {tier?.name ? tier.name : "-"}
                    </p>
                    {tier?.tagline && (
                      <p className="font-heading italic font-light mt-1 text-[hsl(var(--on-ink)/0.6)]">
                        {tier.tagline}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-5 flex items-center gap-2 text-[hsl(var(--on-ink)/0.6)]">
                    <Lock size={16} aria-hidden="true" />
                    <span className="text-sm">In your report</span>
                  </p>
                )}

              </div>
            </div>

            {!revealed ? (
              <>
                <p className="mt-6 max-w-[40rem] leading-relaxed text-[hsl(var(--on-ink)/0.85)]">
                  {getHeadline(selectedOptions)}
                </p>
                <p className="mt-3 max-w-[40rem] text-sm leading-relaxed text-[hsl(var(--on-ink)/0.6)]">
                  Below is your detailed report: the observations behind this score, your primary
                  opportunity area, your infrastructure snapshot and the next step we’d recommend.
                </p>

                <div className="relative mt-8">
                  {/* Blurred, inert preview of the real report */}
                  <div
                    ref={blurredRef}
                    aria-hidden="true"
                    className="relative overflow-hidden"
                    style={{
                      filter: "blur(6px)",
                      opacity: 0.5,
                      pointerEvents: "none",
                      userSelect: "none",
                      maxHeight: "420px",
                    }}
                  >
                    {fullReport}
                  </div>

                  {/* Gradient fade to the section ground */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
                    style={{
                      background: "linear-gradient(to bottom, transparent, hsl(var(--ink)) 88%)",
                    }}
                  />


                  {/* Capture card */}
                  <div className="absolute inset-0 flex items-center justify-center px-4">
                    {/*
                      * p-8 only once there is room for it. On a phone this card
                      * is the narrowest container on the site, and 32px of
                      * padding each side was taking 64px out of a 310px card -
                      * which the spam challenge, a fixed 300px wide, then had
                      * to be scaled to 0.81 to fit inside. Giving it back the
                      * padding raises that to 0.87 at 390 and 0.94 at 412, so
                      * the challenge stays legible. Unchanged from sm up.
                      */}
                    <div
                      className="w-full max-w-[34rem] bg-[hsl(var(--ink-2))] border p-6 sm:p-8"
                      style={{ borderColor: "rgba(255,255,255,0.14)" }}
                    >
                      <p className="font-eyebrow text-[hsl(var(--on-ink)/0.5)]">Your full report</p>
                      <h3
                        className="font-heading font-semibold text-[hsl(var(--on-ink))] mt-3"
                        style={{ fontSize: "clamp(1.35rem, 2.4vw, 1.6rem)", lineHeight: 1.2 }}
                      >
                        See your detailed results
                      </h3>
                      <ul className="mt-5 space-y-2">
                        {[
                          "Your primary opportunity area",
                          "Four key observations on your finances",
                          "Your financial infrastructure snapshot",
                          "The support level that fits your business",
                          "Your recommended next step",
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <Check
                              size={14}
                              className="mt-1 shrink-0 text-[hsl(var(--green-bright))]"
                              aria-hidden="true"
                            />
                            <span className="text-sm leading-relaxed text-[hsl(var(--on-ink)/0.78)]">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <form onSubmit={handleSubmit} noValidate className="mt-6">
                        <label htmlFor="assessment-email" className="sr-only">
                          Your email address
                        </label>
                        {/* Honeypot - hidden from real users */}
                        <input
                          type="text"
                          name="company_website"
                          tabIndex={-1}
                          aria-hidden="true"
                          autoComplete="off"
                          value={honeypot}
                          onChange={(e) => setHoneypot(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            id="assessment-email"
                            type="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              if (emailError) setEmailError("");
                            }}
                            placeholder="you@company.com"
                            aria-invalid={emailError ? true : undefined}
                            aria-describedby={emailError ? "assessment-email-error" : undefined}
                            className="flex-1 rounded-lg px-4 py-3 bg-[rgba(255,255,255,0.05)] text-[hsl(var(--on-ink))] placeholder:text-[hsl(var(--on-ink)/0.55)] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--green-bright))]"
                            style={{
                              borderColor: emailError
                                ? "hsl(var(--amber-bright))"
                                : "rgba(255,255,255,0.22)",
                            }}
                          />
                          <button
                            type="submit"
                            disabled={submitting}
                            className="bg-[hsl(var(--green-bright))] text-[hsl(var(--ink))] px-6 py-3 text-[11px] font-semibold tracking-widest uppercase rounded-lg hover:bg-[hsl(var(--on-ink))] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {submitting ? "Just a moment…" : "Show my report"}
                          </button>
                        </div>
                        {turnstileKey ? (
                          <div
                            className="ozria-turnstile mt-3"
                            data-sitekey={turnstileKey}
                            ref={(el) => {
                              if (!el || el.dataset.wired) return;
                              el.dataset.wired = "1";
                              el.addEventListener("ozria-turnstile", (e) =>
                                setTurnstile(String((e as CustomEvent).detail ?? "")),
                              );
                              /*
                               * The widget script may already have run and
                               * found nothing - this element did not exist
                               * when the page loaded. Asking it to look again
                               * is what draws the challenge.
                               */
                              const draw = (window as unknown as { ozriaTurnstile?: () => void })
                                .ozriaTurnstile;
                              if (draw) draw();
                            }}
                          />
                        ) : null}
                        {emailError && (
                          <p
                            id="assessment-email-error"
                            className="mt-3 text-sm text-[hsl(var(--amber-bright))]"
                          >
                            {emailError}
                          </p>
                        )}
                        <p className="mt-4 text-sm leading-relaxed text-[hsl(var(--on-ink)/0.55)]">
                          We’ll store your answers and email address so we can share your report and
                          follow up about it. No newsletter, no sharing with anyone else. See our{" "}
                          <a
                            href={privacyUrl}
                            className="underline hover:text-[hsl(var(--on-ink)/0.8)]"
                          >
                            privacy policy
                          </a>
                          .
                        </p>

                      </form>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div ref={reportRef} tabIndex={-1} className="focus-visible:outline-none">
                {fullReport}
                {/*
                  * We email a copy of this the moment it is saved, and the
                  * report said nothing about it - so somebody who handed over
                  * an address had no idea anything was coming, and no reason
                  * not to screenshot the page.
                  */}
                {!saveFailed && sentTo !== "" && (
                  <p className="mt-8 text-sm leading-relaxed text-[hsl(var(--on-ink)/0.6)] max-w-[46rem]">
                    A copy of this report is on its way to{" "}
                    <span className="text-[hsl(var(--on-ink))]">{sentTo}</span>. Worth keeping - it
                    is a useful thing to compare against in six months.
                  </p>
                )}
                {saveFailed && (
                  <p className="mt-8 text-sm leading-relaxed text-[hsl(var(--amber-bright))] max-w-[46rem]">
                    We couldn’t save your details just now. Your report above is unaffected - if
                    you’d like us to pick this up with you, email{" "}
                    <a
                      href={`mailto:${contactEmail}`}
                      className="underline hover:text-[hsl(var(--on-ink))]"
                    >
                      {contactEmail}
                    </a>
                    .
                  </p>
                )}

                {/*
                  * Quieter than it was. This is the destructive action and it
                  * was the only button in the report - the same weight as, and
                  * further up than, anything that moves forward.
                  */}
                <button
                  type="button"
                  onClick={reset}
                  className="mt-10 inline-flex items-center gap-2 min-h-[44px] text-sm text-[hsl(var(--on-ink)/0.55)] underline underline-offset-4 hover:text-[hsl(var(--on-ink))] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--green-bright))]"
                >
                  <RotateCcw size={14} aria-hidden="true" /> Start the assessment again
                </button>
              </div>
            )}

          </div>
        )}

        {/* Always-available discovery call CTA */}
        <div
          className="mt-14 flex flex-wrap items-center justify-between gap-6 pt-10 border-t"
          style={{ borderColor: "rgba(255,255,255,0.12)" }}
        >
          <p className="max-w-[34rem] text-sm leading-relaxed text-[hsl(var(--on-ink)/0.6)]">
            Ready to talk it through? You don’t need to complete the assessment first.
          </p>
          <a
            href={bookingUrl}
            className="inline-flex items-center gap-2 bg-[hsl(var(--green-bright))] text-[hsl(var(--ink))] px-8 py-4 text-[11px] font-semibold tracking-widest uppercase rounded-lg hover:bg-[hsl(var(--on-ink))] transition-colors"
          >
            Book a discovery call <ArrowRight size={16} />
          </a>
        </div>

      </div>
    </section>
  );
};

export default ReadinessAssessment;
